/**
 * web/src/screens/Settings/you.tsx — the You section of Settings (card
 * MudceTexpbq1): the partner invite, the two names, reply colour, birthday,
 * partner digest, weekly recap, interests and notifications — moved verbatim
 * out of index.tsx. State and saves stay in the screen and arrive as props.
 */
import type { Dispatch, SetStateAction } from 'react';
import { InviteCard } from '../../components/InviteCard';
import { Switch } from '../../ui/Switch';
/* Card psHEfEkK9cT2 — the confetti preference, one key shared with the layer
   in App, so the switch and the burst can never disagree. */
import { setConfettiEnabled, useConfettiEnabled } from '../../lib/celebrate';
import { REPLY_COLOURS, replyColourHex } from '../../../../shared/types';
import { partnerLabel, partnerPossessive } from '../../lib/partnerName';
import { isDate, recapWhen } from './helpers';
import { Interests } from './Interests';
import { NotificationsSection } from './notifications';
import type { Profile, RecapState } from './types';

export function YouSection({
  awaitingPartner, pendingCode, mailEnabled, nameBlocked, realName, saveName,
  chatName, saveChatName, profile, setProfile, save, digestFreq, saveDigestFrequency,
  recap, recapNote, saving, setRecapEnabled,
}: {
  awaitingPartner?: boolean;
  pendingCode?: string | null;
  mailEnabled?: boolean;
  nameBlocked: string | null;
  realName: string | null;
  saveName: (value: string) => Promise<void>;
  chatName: string | null;
  saveChatName: (value: string) => Promise<void>;
  profile: Profile | null;
  setProfile: Dispatch<SetStateAction<Profile | null>>;
  save: (which: 'profile' | 'joint', body: Record<string, unknown>) => Promise<void>;
  digestFreq: number;
  saveDigestFrequency: (frequency: number) => Promise<void>;
  recap: RecapState | null;
  recapNote: string | null;
  saving: string | null;
  setRecapEnabled: (on: boolean) => Promise<void>;
}) {
  /* The saved reply ink, resolved once — the row names it and the swatch ring
     repeats it (founder email 2026-10-28: "colour doesn't stick"). */
  const pickedReplyColour = REPLY_COLOURS.find((c) => c.key === (profile?.replyColour ?? null)) ?? null;
  /* Card psHEfEkK9cT2 — confetti on a shared win, on by default. */
  const confettiOn = useConfettiEnabled();

  return (
    <>
        {/* ── Invite, until it is accepted (Jonathan, pm admin 2026-10-13) ──
            The chat share bar is the only place the invite used to live, and
            it disappears the moment you navigate. Settings is the screen people
            come back to, so the same card sits at the top of the You tab for
            as long as the couple is still one member: Copy / Share / Email /
            QR, and the waiting line that says what is still outstanding.
            Leading the identity tab because pairing IS the identity to-do. */}
        {awaitingPartner && pendingCode && (
          <section className="set-section" aria-labelledby="set-invite-title">
            <h3 className="set-section-title" id="set-invite-title">
              Invite your partner
            </h3>
            <p className="set-section-note">
              This stays here until they join — send it however suits you, as
              often as you like.
            </p>
            <InviteCard
              code={pendingCode}
              mailEnabled={Boolean(mailEnabled)}
              idPrefix="settings-invite"
              variant="pair"
            />
          </section>
        )}

        {/* ── You: identity, one person's own rows ─────────────────────── */}
        <section className="set-section" aria-labelledby="set-you-title">
          <h3 className="set-section-title" id="set-you-title">
            You
          </h3>

          {/* ── The two names (card yFLCjm7ONIEc). Two questions, two fields:
              the real name is the account's own record and never enters the
              thread; the display name is the room's members row, which is what
              the partner actually sees. Both autosave per keystroke. ─────── */}
          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Name</div>
              <div className="set-note">Your real name — account records only, never shown in the chat.</div>
            </div>
            {nameBlocked ? (
              <p className="set-note set-name-blocked" id="set-name-blocked">{nameBlocked}</p>
            ) : realName === null ? (
              <p className="set-loading" aria-busy="true">Loading…</p>
            ) : (
              <input
                type="text"
                id="set-name"
                className="set-name-input"
                aria-label="Your name"
                autoComplete="name"
                value={realName}
                onChange={e => void saveName(e.target.value)}
              />
            )}
          </div>

          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Display name</div>
              <div className="set-note">What {partnerLabel()} sees in the thread and in presence.</div>
            </div>
            <input
              type="text"
              id="set-display-name"
              className="set-name-input"
              aria-label="Display name"
              value={chatName ?? ''}
              onChange={e => void saveChatName(e.target.value)}
            />
          </div>

          {/* Reply-text colour (card 9QADJv3CdiOh, founder email 2026-10-23):
              the ink YOUR words wear in the partner's feed. The swatches are
              the closed shared list — each one measured AA on the mustard
              partner bubble — and the preview is the partner's own view of a
              bubble, so what is picked is what they will read. One PUT per
              click through the same save() as the birthday row; a refusal
              lands in the status line and load() restores the server's truth. */}
          <div className="set-row set-colour-row">
            <div className="set-text">
              <div className="set-label">Reply colour</div>
              <div className="set-note">The colour your replies wear in {partnerPossessive()} feed.</div>
              <div className="set-colour-preview" aria-hidden="true">
                <span
                  className="set-colour-preview-bubble"
                  style={{ color: replyColourHex(profile?.replyColour ?? null) ?? '#1F3B2D' }}
                >
                  Your words, their feed.
                </span>
              </div>
              {/* Founder email 2026-10-28 (msg_c09b631e): "colour doesn't
                  stick" — the ring alone was the only evidence of a pick.
                  Name the saved colour, from the server's own profile. */}
              <div className="set-colour-current" aria-live="polite">
                {pickedReplyColour
                  ? `Selected: ${pickedReplyColour.label}.`
                  : 'Nothing picked yet — your replies use the default ink.'}
              </div>
            </div>
            <div className="set-colour-grid" role="radiogroup" aria-label="Reply colour">
              {REPLY_COLOURS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={(profile?.replyColour ?? null) === c.key}
                  aria-label={c.label}
                  title={c.label}
                  className={`set-colour-swatch${profile?.replyColour === c.key ? ' is-on' : ''}`}
                  style={{ background: c.hex }}
                  onClick={() => void save('profile', { replyColour: c.key })}
                />
              ))}
            </div>
          </div>

          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Birthday</div>
              <div className="set-note">Yours only — {partnerLabel()} keeps their own.</div>
            </div>
            <input
              type="date"
              id="set-birthday"
              className="set-date"
              aria-label="Your birthday"
              value={profile?.birthday ?? ''}
              onChange={e => {
                const value = e.target.value || null;
                if (value && !isDate(value)) return;
                setProfile(p =>
                  p
                    ? { ...p, birthday: value }
                    : { memberId: '', birthday: value, displayName: chatName ?? null },
                );
                void save('profile', { birthday: value });
              }}
            />
          </div>

          {/* Partner digest cadence (card g3ixXFWzhgJY) — the JitsLab
              digest_frequency shape: 0 is off, and off is a complete answer. */}
          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Partner digest</div>
              <div className="set-note">How often one arrives in Us, built from {partnerPossessive()} interests.</div>
            </div>
            <select
              id="set-digest-frequency"
              className="set-date"
              aria-label="Partner digest frequency"
              value={digestFreq}
              onChange={e => void saveDigestFrequency(Number(e.target.value))}
            >
              <option value={0}>Off</option>
              {[1, 2, 3, 4, 5, 6, 7].map(n => (
                <option key={n} value={n}>{n}× a week</option>
              ))}
            </select>
          </div>

          {/* Weekly recap (card 3UwJvUtiirGN, Jonathan email 2026-10-07) —
              opt-in, one email a week to each of you, each with their own half
              of the week. The toggle is the 'recap' permission, so the
              concierge screen shows the same state. */}
          <div className="set-row" id="recap-row">
            <div className="set-text">
              <div className="set-label">Weekly recap</div>
              <div className="set-note">
                One email a week: questions answered, decisions taken, links filed. Yours only — {partnerLabel()} gets their own.
              </div>
              {recap?.enabled && (
                <p className="set-note" id="recap-next" data-testid="recap-next">{recapWhen(recap)}</p>
              )}
              {recapNote && (
                <p className="set-note" id="recap-note">{recapNote}</p>
              )}
            </div>
            <Switch
              checked={Boolean(recap?.enabled)}
              label="Weekly recap"
              disabled={saving === 'recap' || recap === null}
              onChange={on => void setRecapEnabled(on)}
            />
          </div>

          {/* Confetti (card psHEfEkK9cT2, Jonathan email 2026-11-04) — the
              burst belongs to a shared win, so the switch that silences it
              lives beside the other "how this feels" rows in You. One key
              (pear.settings.confetti) is read by the layer itself, so off
              means off everywhere. */}
          <div className="set-row" id="confetti-row">
            <div className="set-text">
              <div className="set-label">Confetti</div>
              <div className="set-note">A burst when you win a game together. Off keeps the moment quiet.</div>
            </div>
            <Switch checked={confettiOn} label="Confetti" onChange={setConfettiEnabled} />
          </div>

          {/* Interests profile (card 0OvyJbpnACMX) — the agent's four
              questions, one at a time, then the topic chips it builds. It is
              YOUR profile, so it lives on this tab. */}
          <section className="set-section" aria-labelledby="set-interests-title">
            <h3 className="set-section-title" id="set-interests-title">
              Your interests
            </h3>
            <Interests />
          </section>

          {/* Notifications (card HWrVePJ5qW4d slice 2) — the three triggers. */}
          <NotificationsSection />
        </section>
    </>
  );
}
